import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Modal, message } from 'antd';
import { Formik } from 'formik';
import { omit } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import * as permissionKind from '../../../../../shared/permissions';
import { useUpdateInsuranceProductMutation } from '../../../../api';
import { useDeleteInsuranceProductMutation } from '../../../../api/mutations/deleteInsuranceProduct';
import getApolloErrors from '../../../../utilities/getApolloErrors';
import hasPermissions from '../../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../../utilities/useFormattedSimpleVersioning';
import useHandleError from '../../../../utilities/useHandleError';
import useInsuranceProductValidation from '../useInsuranceProductValidation';
import InsuranceProductForm from './InsuranceProductForm';
import {
    type InsuranceProductDetailsInnerPageProps,
    type InsuranceProductFormValues,
    getUpdateInitialValue,
} from './shared';

const InsuranceProductDetailsInnerPage = ({ insurance }: InsuranceProductDetailsInnerPageProps) => {
    const { t } = useTranslation('insuranceProductDetails');
    const initialValues = getUpdateInitialValue(omit(['permissions', 'variants'], insurance));
    const navigate = useNavigate();

    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: insurance.versioning,
        moduleId: insurance.module.id,
    });
    const [useUpdateInsuranceProduct] = useUpdateInsuranceProductMutation();
    const [useDeleteInsuranceProduct] = useDeleteInsuranceProductMutation();

    const validate = useInsuranceProductValidation(true);

    const onSubmit = useHandleError<InsuranceProductFormValues>(
        async values => {
            // submitting message
            message.loading({
                content: t('insuranceProductDetails:messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            const { id, module, moduleId, versioning, insurer, ...otherValues } = values;

            await useUpdateInsuranceProduct({
                variables: {
                    id: insurance.id,
                    settings: otherValues,
                },
            });

            // inform about success
            message.success({
                content: t('insuranceProductDetails:messages.updateSuccessful'),
                key: 'primary',
            });

            navigate(-1);
        },
        [t, useUpdateInsuranceProduct, insurance.id, navigate]
    );
    const deleteInsuranceProduct = useCallback(() => {
        Modal.confirm({
            title: t('insuranceProductDetails:deleteModal.title'),
            icon: <ExclamationCircleOutlined />,
            content: t('insuranceProductDetails:deleteModal.content'),
            okText: t('insuranceProductDetails:deleteModal.okText'),
            okType: 'danger',
            cancelText: t('insuranceProductDetails:deleteModal.cancelText'),
            async onOk() {
                try {
                    // loading message
                    message.loading({
                        content: t('insuranceProductDetails:messages.deleteSubmitting'),
                        key: 'primary',
                        duration: 0,
                    });

                    await useDeleteInsuranceProduct({
                        variables: { id: insurance.id },
                    });

                    // show success
                    message.success({
                        content: t('insuranceProductDetails:messages.deleteSuccessful'),
                        key: 'primary',
                    });

                    // then go back to list
                    navigate('/admin/insuranceProducts');
                } catch (error) {
                    const apolloErrors = getApolloErrors(error);

                    if (apolloErrors?.$root) {
                        message.error(apolloErrors?.$root);
                    }
                }
            },
        });
    }, [insurance.id, navigate, t, useDeleteInsuranceProduct]);

    const permissions = useMemo(
        () => ({
            hasUpdatePermission: hasPermissions(insurance.permissions, [permissionKind.updateInsuranceProduct]),
            hasDeletePermission: hasPermissions(insurance.permissions, [permissionKind.deleteInsuranceProduct]),
        }),
        [insurance.permissions]
    );

    return (
        <Formik<InsuranceProductFormValues> initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <InsuranceProductForm
                    deleteInsuranceProduct={deleteInsuranceProduct}
                    handleSubmit={handleSubmit}
                    insuranceProduct={insurance}
                    offset={offset}
                    permissions={permissions}
                    updated={updated}
                />
            )}
        </Formik>
    );
};

export default InsuranceProductDetailsInnerPage;
